import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Input, Text, VStack, Flex, Button, ButtonProps, useToast, InputGroup, InputRightElement, Spinner, Skeleton } from '@chakra-ui/react';
import { FiMapPin, FiNavigation } from 'react-icons/fi';
import { FoodfluenceLocation } from '@foodfluence/shared';
import { calculateDistance, getCurrentLocation, getInfluencerPrimaryLocation } from '../../../../utils/locationUtils';
import { geocodePostalCode } from '../../../../services/api/location';
import { InfluencerLocationContext } from '../../../../contexts/InfluencerLocationContext';
import { InfluencerDataContext } from '../../../../contexts/InfluencerDataContext';
import { wizardTokens } from '../styles';
import EditZipModal from './EditZipModal';
import FoodfluenceLoader from '../../../../components/FoodfluenceLoader';

// Board BookVisit step 2: outlined pill for "See more", eyebrow section heads.
const OUTLINE_PILL: ButtonProps = {
  alignSelf: 'center',
  h: '48px',
  px: '18px',
  borderRadius: '999px',
  border: '1.5px solid #0E0E0E',
  bg: '#FFFFFF',
  color: '#0E0E0E',
  fontFamily: 'display',
  fontSize: '15px',
  fontWeight: 600,
  _hover: { bg: '#F2F2F0' },
};
const SECTION = {
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: '#66666A',
};

const zipStorageKey = (influencerId?: string) => (influencerId ? `ff_scheduling_default_zip_${influencerId}` : null);
const readStoredZip = (influencerId?: string): string | null => {
  const key = zipStorageKey(influencerId);
  if (!key) return null;
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeStoredZip = (influencerId: string | undefined, zip: string) => {
  const key = zipStorageKey(influencerId);
  if (!key) return;
  try { localStorage.setItem(key, zip); } catch { /* private mode / quota — ignore */ }
};

interface LocationSearchBoxProps {
  locations: FoodfluenceLocation[];
  locationSearchQuery: string;
  selectedLocationId?: string;
  onLocationSearch: (query: string) => void;
  onLocationSelect: (location: { docId: string; fullAddress: string }) => void;
  isLoading?: boolean;
}

const DEFAULT_VISIBLE = 4;
// Extra venues revealed per "See more locations" tap. Brands can have hundreds, so this pages
// through the ranked list rather than stopping at a fixed ceiling.
const PAGE_SIZE = 20;

const LocationSearchBox: React.FC<LocationSearchBoxProps> = ({
  locations,
  locationSearchQuery,
  selectedLocationId,
  onLocationSearch,
  onLocationSelect,
  isLoading = false,
}) => {
  const { influencerLocations } = useContext(InfluencerLocationContext);
  const { influencerData } = useContext(InfluencerDataContext);
  const influencerId = influencerData?.id;

  const [userZipLocation, setUserZipLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  // How many ranked venues are rendered. Grows a page at a time; resets on pick (which collapses
  // the list back to the chosen card).
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE);
  const isExpanded = visibleCount > DEFAULT_VISIBLE;
  const showMoreLocations = () => setVisibleCount((c) => Math.max(c, DEFAULT_VISIBLE) + PAGE_SIZE);
  // True only when the reference point comes from a real signal: geolocation, a resolved default
  // zip, or a typed search. Profile fallback may still SORT; distance text + "Closest to you"
  // only render when this is true.
  const [hasLiveLocation, setHasLiveLocation] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  // The zip currently driving the sort (shown as "Based on <zip>"); default zip is the persisted one.
  const [defaultZip, setDefaultZip] = useState<string | null>(null);
  const [activeZipLabel, setActiveZipLabel] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [savedZip, setSavedZip] = useState<string | null>(null);
  // True once the creator explicitly overrides the default (search, edit sheet or current-location).
  // While true, the reactive default-zip resolver yields and won't clobber their choice.
  const userOverrodeRef = useRef(false);

  // Reference location: prefer resolved coords (default zip / search / geolocation), else the
  // influencer's primary profile location (sort only).
  const referenceCoords = userZipLocation ?? (() => {
    const primary = getInfluencerPrimaryLocation(influencerLocations);
    if (primary && primary.lat !== 0 && primary.lng !== 0) {
      return { lat: primary.lat, lng: primary.lng };
    }
    return null;
  })();

  // Geocode a zip/place to coords (null if unresolved).
  const geocodeZip = async (zip: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const results = await geocodePostalCode(zip, 'USA');
      if (results && results[0]) return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lng) };
    } catch (error) {
      console.error('Error geocoding zip:', error);
    }
    return null;
  };

  // Apply a zip/place as the active reference (sort basis + "Based on" label).
  const applyZip = async (zip: string) => {
    setIsGeocoding(true);
    const coords = await geocodeZip(zip);
    if (coords) {
      setUserZipLocation(coords);
      setHasLiveLocation(true);
      setActiveZipLabel(zip);
    }
    setIsGeocoding(false);
  };

  // Read the persisted default zip once the influencer id is known.
  useEffect(() => {
    setSavedZip(readStoredZip(influencerId));
  }, [influencerId]);

  // Resolve the default basis reactively: saved zip > primary profile zip. Re-runs as profile data
  // streams in (idempotent), and yields to any manual override (search / edit / current-location).
  useEffect(() => {
    if (userOverrodeRef.current || locationSearchQuery.trim()) return;
    const primary = getInfluencerPrimaryLocation(influencerLocations);
    const zip = savedZip ?? primary?.zipCode ?? null;
    if (!zip || zip === activeZipLabel) return;
    setDefaultZip(zip);
    setActiveZipLabel(zip);
    setHasLiveLocation(true);
    if (savedZip) {
      void applyZip(savedZip);                                   // only a string — geocode it
    } else if (primary?.lat && primary?.lng) {
      setUserZipLocation({ lat: primary.lat, lng: primary.lng }); // profile coords already on hand
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedZip, influencerLocations, locationSearchQuery]);

  // Search: debounced-geocode the typed place so the list re-sorts closest→farthest. Never
  // substring-match venue names/addresses. A resolved search becomes the sticky basis — it is NOT
  // reverted when the box empties, because the wizard clears the box itself on pick; reverting
  // there would yank the reference point back home and drop the just-chosen venue out of the
  // nearest-N window. The "Based on <place>" label + edit pencil is how the basis changes.
  useEffect(() => {
    const q = locationSearchQuery.trim();
    // Emptying the box (typing back to blank, or the wizard clearing it on pick) cancels the
    // pending debounce — clear the flag too, or the sorting indicator spins forever.
    if (!q) { setIsGeocoding(false); return; }
    setIsGeocoding(true);
    const t = setTimeout(async () => {
      const coords = await geocodeZip(q);
      if (coords) {
        userOverrodeRef.current = true;   // keep the default-zip resolver from clobbering the search
        setUserZipLocation(coords);
        setHasLiveLocation(true);
        setActiveZipLabel(q);
      }
      setIsGeocoding(false);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearchQuery]);

  const toast = useToast();

  // Save from the edit sheet: always apply for this session; persist only if "set as default".
  const handleSaveZip = async (zip: string, setAsDefault: boolean) => {
    userOverrodeRef.current = true;
    setDefaultZip(zip);
    if (setAsDefault) { writeStoredZip(influencerId, zip); setSavedZip(zip); }
    onLocationSearch('');
    await applyZip(zip);
    setIsEditOpen(false);
  };

  // No locations to pick from (and not loading): explain why + point to support, rather than
  // rendering nothing and stranding the creator on a disabled Continue.
  if (!isLoading && (!locations || locations.length === 0)) {
    return (
      <Box p="18px" borderRadius="18px" bg="#F2F2F0" fontFamily="display">
        <Text fontSize="16px" fontWeight={600} color={wizardTokens.ink}>No locations available yet</Text>
        <Text fontSize="14px" lineHeight="1.4" color="#3E3E42" mt="4px">
          This brand hasn't added any locations for this collaboration. Reach out to support if you
          think this is a mistake.
        </Text>
      </Box>
    );
  }

  const handleCurrentLocation = async () => {
    setIsLoadingLocation(true);
    try {
      const locationData = await getCurrentLocation();
      userOverrodeRef.current = true;
      setUserZipLocation({ lat: locationData.lat, lng: locationData.lng });
      setHasLiveLocation(true);
      setActiveZipLabel(null);   // device location has no zip to show as "Based on"
      onLocationSearch('');
    } catch (error) {
      console.error('Error getting current location:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Could not access your location',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Every venue, annotated + ranked. Ranking the full list (not a pre-sliced window) is what lets
  // the selected venue always be found below, however far it is from the current basis.
  const rankLocations = (): (FoodfluenceLocation & { distance: number })[] => {
    if (isLoading || !locations) return [];

    // Never filter the venue list by the query — search only re-sorts by distance to the
    // geocoded place (see the search effect).
    const ranked = locations.map(location => ({
      ...location,
      distance: referenceCoords
        ? calculateDistance(referenceCoords.lat, referenceCoords.lng, location.lat, location.lng)
        : 0,
    }));
    if (referenceCoords) {
      // A venue with a missing/zeroed geocode yields NaN, and a comparator that returns NaN leaves
      // the whole order arbitrary — so those sort last instead of scrambling the window.
      const rank = (d: number) => (Number.isFinite(d) ? d : Number.MAX_VALUE);
      ranked.sort((a, b) => rank(a.distance) - rank(b.distance));
    }
    return ranked;
  };

  const rankedLocations = rankLocations();
  const selectedLoc = selectedLocationId
    ? rankedLocations.find((l) => l.docId === selectedLocationId)
    : undefined;
  // Paged, not capped. A fixed ceiling meant a brand with hundreds of venues ran out after one tap
  // of "See more locations" — the button vanished and everything past the ceiling was unreachable.
  const page = rankedLocations.slice(0, visibleCount);
  // The chosen venue always leads the list. That keeps it from falling out of the window when the
  // reference point moves (which used to drop the selected card and its tick, leaving Step 2
  // looking like nothing was picked), and it puts it under the "Your location" heading below.
  const visible = selectedLoc
    ? [selectedLoc, ...page.filter((l) => l.docId !== selectedLoc.docId)]
    : page;
  const hasMoreLocations = visibleCount < rankedLocations.length;
  // Once a location is chosen, collapse the list to just that card; a "See more locations" toggle
  // stays available (when there's more than one) so the creator can reopen and swap. But never
  // collapse while the creator is typing a search — the filtered matches must stay visible.
  const collapsed = !!selectedLoc && !isExpanded && !locationSearchQuery.trim();

  // The sort basis is always editable. Device geolocation has no zip to name and an unresolved
  // basis has nothing at all — but those are exactly the cases where the creator most needs a way
  // to set one, so the pencil renders in every state rather than riding on a label being present.
  const basisRow = (
    <Flex p="4px 4px 4px 14px" borderRadius="14px" bg="#F2F2F0" align="center" gap="10px">
      <Box as="span" flex="none" aria-hidden><FiMapPin size={18} strokeWidth={2.2} /></Box>
      <Text flex={1} fontSize="14px" color="#3E3E42" lineHeight="normal">
        {activeZipLabel ? (
          `Based on ${activeZipLabel}`
        ) : hasLiveLocation
          ? 'Based on your current location'
          : 'Set a zip code'}
      </Text>
      <Button
        variant="unstyled"
        aria-label="Edit default zip code"
        h="44px"
        px="12px"
        fontFamily="display"
        fontSize="14px"
        fontWeight={600}
        color={wizardTokens.ink}
        textDecoration="underline"
        onClick={() => setIsEditOpen(true)}
      >
        Edit area
      </Button>
    </Flex>
  );

  const renderCard = (location: FoodfluenceLocation & { distance: number }, _isClosest: boolean) => {
    const isSelected = selectedLocationId === location.docId;
    return (
      <Flex
        as="button"
        type="button"
        aria-pressed={isSelected}
        key={location.docId}
        onClick={() => { onLocationSelect({ docId: location.docId, fullAddress: location.fullAddress }); setVisibleCount(DEFAULT_VISIBLE); }}
        align="flex-start"
        gap="12px"
        textAlign="left"
        w="100%"
        minH="80px"
        p="14px"
        borderRadius="16px"
        border={isSelected ? `2px solid ${wizardTokens.ink}` : '1px solid #DCDCD8'}
        bg={isSelected ? '#F1FADF' : '#FFFFFF'}
        color={wizardTokens.ink}
        cursor="pointer"
      >
        <Box
          as="span"
          flex="none"
          w="22px"
          h="22px"
          boxSizing="border-box"
          borderRadius="999px"
          border={isSelected ? `7px solid ${wizardTokens.ink}` : '2px solid #9A9A9E'}
          bg="#FFFFFF"
        />
        <Flex flex={1} minW={0} direction="column" gap="3px">
          <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>
            {location.name || location.fullAddress}
          </Text>
          <Text fontSize="13px" lineHeight="1.35" color="#55555A" noOfLines={2}>{location.fullAddress}</Text>
        </Flex>
        {isGeocoding ? (
          <Skeleton flex="none" height="16px" width="44px" borderRadius="999px" startColor="#F2F2F0" endColor="#DCDCD8" />
        ) : hasLiveLocation && Number.isFinite(location.distance) ? (
          <Text flex="none" fontSize="13px" fontWeight={600} color="#3E3E42" lineHeight="normal">
            {location.distance.toFixed(1)} mi
          </Text>
        ) : null}
      </Flex>
    );
  };

  return (
    <VStack spacing="10px" align="stretch" fontFamily="display">
      {basisRow}
      <InputGroup>
        <Input
          placeholder={isLoading ? 'Loading locations...' : 'Search by city or ZIP to sort by distance'}
          value={locationSearchQuery}
          h="48px"
          px="14px"
          borderRadius="14px"
          border="1px solid #DCDCD8"
          bg="#FFFFFF"
          fontSize="16px"
          color={wizardTokens.ink}
          _placeholder={{ color: '#9A9A9E' }}
          _hover={{ borderColor: '#DCDCD8' }}
          _focusVisible={{ borderColor: wizardTokens.ink, boxShadow: 'none' }}
          onChange={(e) => onLocationSearch(e.target.value)}
          type="text"
          pr="3rem"
          isDisabled={isLoading}
        />
        <InputRightElement width="3rem" h="48px">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Button
              h="44px"
              minW="44px"
              p={0}
              mt="4px"
              borderRadius="999px"
              onClick={handleCurrentLocation}
              isLoading={isLoadingLocation}
              variant="ghost"
              color={wizardTokens.ink}
              _hover={{ bg: '#F2F2F0' }}
              title="Use current location"
              aria-label="Use current location"
            >
              <FiNavigation size={18} strokeWidth={2.2} />
            </Button>
          )}
        </InputRightElement>
      </InputGroup>

      {/* Re-sorting after a search/zip change is a round trip to the geocoder, and the list below
          silently reshuffles when it lands. Say so, rather than leaving the creator guessing. */}
      {isGeocoding && !isLoading ? (
        <Flex align="center" gap={2}>
          <FoodfluenceLoader size="18px" label="Sorting locations" />
          <Text fontSize="13px" color="#55555A">Sorting locations by distance…</Text>
        </Flex>
      ) : !hasLiveLocation && !isLoading ? (
        <Text fontSize="13px" lineHeight="1.4" color="#55555A">
          Enable location access or search by city / ZIP to sort spots by distance.
        </Text>
      ) : null}

      {isLoading ? (
        <Box p={4} textAlign="center">
          <Spinner size="sm" />
          <Text mt={2} fontSize="14px" color="#55555A">Loading locations...</Text>
        </Box>
      ) : collapsed && selectedLoc ? (
        // Chosen: show only the selected card, keep a toggle to reopen the full list and swap.
        <VStack spacing={3} align="stretch">
          <Text as="h2" m={0} {...SECTION}>Your location</Text>
          {renderCard(selectedLoc, false)}
          {rankedLocations.length > 1 && (
            <Button {...OUTLINE_PILL} onClick={showMoreLocations}>
              See more locations
            </Button>
          )}
        </VStack>
      ) : visible.length > 0 ? (
        <VStack spacing={3} align="stretch">
          {/* Once something is picked, the lead card IS their location — say so. Otherwise the
                  lead card is the nearest one, and "you" is only honest when the basis is actually
                  where they are: device geolocation is the one basis with no place label, since a
                  searched or saved zip is a place rather than them. */}
              <Text as="h2" m={0} {...SECTION}>
                {selectedLoc
                  ? 'Your location'
                  : !hasLiveLocation
                    ? 'Locations'
                    : activeZipLabel
                      ? 'Closest to your location'
                      : 'Closest to you'}
              </Text>
          {renderCard(visible[0], hasLiveLocation)}

          {visible.length > 1 && (
            <>
              <Text as="h2" m={0} pt="6px" {...SECTION}>
                {hasLiveLocation ? 'More locations near you' : 'Locations'}
              </Text>
              {visible.slice(1).map((loc) => renderCard(loc, false))}
            </>
          )}

          {/* End-of-list status. The list grows downward, so the "we're working" signal has to live
              here as well as under the search box — by the time the creator has scrolled through a
              page of venues, the header indicator is long off-screen. */}
          {isGeocoding ? (
            <Flex align="center" justify="center" gap={2} py={2}>
              <FoodfluenceLoader size="20px" label="Sorting locations" />
              <Text fontSize="14px" color="#55555A">Sorting locations by distance…</Text>
            </Flex>
          ) : hasMoreLocations ? (
            <Button {...OUTLINE_PILL} onClick={showMoreLocations}>
              See more locations ({rankedLocations.length - visibleCount} more)
            </Button>
          ) : null}
        </VStack>
      ) : (
        <Box p={4} textAlign="center">
          <Text fontSize="14px" color="#55555A">
            No locations available
          </Text>
        </Box>
      )}

      <EditZipModal
        isOpen={isEditOpen}
        initialZip={defaultZip ?? activeZipLabel ?? ''}
        onSave={handleSaveZip}
        onClose={() => setIsEditOpen(false)}
      />
    </VStack>
  );
};

export default LocationSearchBox;
